/**
 * Caricamento di una risorsa remota con i tre stati che ogni pagina deve saper
 * mostrare: in corso, errore, dato.
 *
 * Non e' un sostituto di una libreria di data fetching: e' il minimo necessario
 * perche' nessuna pagina renda una tabella vuota mentre in realta' sta ancora
 * caricando, o silenzi un errore dell'API. La richiesta viene annullata quando
 * il componente si smonta o cambiano le dipendenze, cosi' l'ultima risposta
 * ricevuta e' sempre quella dell'ultima richiesta partita.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ErroreApi } from "../api/client";

export interface StatoRisorsa<T> {
  dato: T | null;
  inCaricamento: boolean;
  errore: ErroreApi | null;
  /** Riesegue la richiesta, per esempio dopo una modifica. */
  ricarica: () => void;
}

/**
 * @param carica funzione che esegue la richiesta; riceve un `AbortSignal`.
 * @param dipendenze quando cambiano, la risorsa viene ricaricata.
 */
export function useRisorsa<T>(
  carica: (segnale: AbortSignal) => Promise<T>,
  dipendenze: readonly unknown[] = [],
): StatoRisorsa<T> {
  const [dato, setDato] = useState<T | null>(null);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [errore, setErrore] = useState<ErroreApi | null>(null);
  const [contatore, setContatore] = useState(0);

  // La funzione di caricamento cambia identita' a ogni render: la teniamo in un
  // ref cosi' che a governare il ricaricamento siano solo le dipendenze dichiarate.
  // L'assegnazione avviene in un effetto — non durante il render — e questo
  // effetto e' dichiarato per primo, quindi il ref e' gia' aggiornato quando
  // l'effetto di caricamento parte.
  const caricaRef = useRef(carica);
  useEffect(() => {
    caricaRef.current = carica;
  });

  useEffect(() => {
    const controllore = new AbortController();
    let attivo = true;
    setInCaricamento(true);
    setErrore(null);

    caricaRef
      .current(controllore.signal)
      .then((risultato) => {
        if (!attivo) return;
        setDato(risultato);
      })
      .catch((causa: unknown) => {
        if (!attivo || controllore.signal.aborted) return;
        setErrore(
          causa instanceof ErroreApi
            ? causa
            : new ErroreApi("ERRORE_INTERNO", "Si e' verificato un errore imprevisto.", 0),
        );
      })
      .finally(() => {
        if (attivo) setInCaricamento(false);
      });

    return () => {
      attivo = false;
      controllore.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dipendenze, contatore]);

  const ricarica = useCallback(() => setContatore((valore) => valore + 1), []);

  return { dato, inCaricamento, errore, ricarica };
}
